// Reveal & Predict slide — Phase 2 React port of kula-create's reveal-predict.html.
//
// Pedagogy: misconception buster. Learner predicts, then sees the answer.
// The gap between expectation and reality strengthens encoding.
//
// Each prediction lock fires one onActivityResponse with
//   { activityType: 'reveal-predict', mode: 'quiz', responseData: { predictionId, chosen }, isCorrect, score }
// so the dual-write records one learning_interactions row per prediction
// (activity_key = prediction id, correctness + score populated server-side).

import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { AnimatePresence } from 'framer-motion';
import type { RevealPredictSlideData, Theme, ExperienceConclusion } from '../../slideTypes';
import { slideTypographyStyle } from '../../slideTypes';
import { feedbackStyle } from '../../shared/SlideShared';
import { Reveal } from '../../shared/Reveal';
import { ExperienceClose } from '../../shared/ExperienceClose';
import type { ActivityResponsePayload } from '../../../../../types';

interface Props {
  data: RevealPredictSlideData;
  theme: Theme;
  isEditing: boolean;
  onUpdate: (data: RevealPredictSlideData) => void;
  onActivityResponse?: (response: ActivityResponsePayload) => void;
  onComplete?: () => void;
  savedResponse?: Record<string, unknown> | null;
}

interface RuntimeState {
  selections: Record<string, string>; // predictionId -> chosen value
  locked: Record<string, boolean>;     // predictionId -> locked?
  currentIndex: number;
}

// savedResponse stays in Props (SlideCanvas passes it uniformly) but is
// currently unconsumed: the standard renderer never rehydrated, and the only
// consumer was the removed arcade branch.
export function RevealPredictSlide({ data, theme, isEditing, onUpdate, onActivityResponse, onComplete }: Props) {
  if (isEditing) {
    return <RevealPredictEditor data={data} theme={theme} onUpdate={onUpdate} />;
  }
  // No bespoke hyperpop branch, deliberately. The arcade renderer
  // (HyperpopRevealPredict, removed with this change — git history has it)
  // scored and streaked predictions, which grades the guess; reveal-predict's
  // pedagogy is that a wrong prediction IS the learning signal, and the July
  // audit condemned the treatment (§8.6). Dylan's 9 July ruling swaps the
  // flagship pairings (hyperpop ↔ spot-the-error, neon-oracle ↔
  // reveal-predict); the swap itself is deferred until these types queue for
  // Workshop promotion (KULA-CREATE-LIBRARY-PLAN.md), so until then hyperpop
  // reveal-predict takes the standard themed render like every non-flagship
  // world — plain beats wrong.
  return <RevealPredictStudent data={data} theme={theme} onActivityResponse={onActivityResponse} onComplete={onComplete} />;
}

// ---------------------------------------------------------------------------
// Student-facing renderer
// ---------------------------------------------------------------------------

function RevealPredictStudent({
  data,
  theme,
  onActivityResponse,
  onComplete,
}: {
  data: RevealPredictSlideData;
  theme: Theme;
  onActivityResponse?: (response: ActivityResponsePayload) => void;
  onComplete?: () => void;
}) {
  const [state, setState] = useState<RuntimeState>({
    selections: {},
    locked: {},
    currentIndex: 0,
  });

  const total = data.predictions.length;
  const completed = Object.keys(state.locked).length;
  const allComplete = completed === total && total > 0;

  const completedRef = useRef(false);
  useEffect(() => {
    if (allComplete && !completedRef.current) {
      completedRef.current = true;
      onComplete?.();
    }
  }, [allComplete, onComplete]);

  const score = useMemo(
    () =>
      data.predictions.filter(
        (p) => state.locked[p.id] && state.selections[p.id] === p.correctValue,
      ).length,
    [data.predictions, state.locked, state.selections],
  );

  const select = (predId: string, value: string) => {
    if (state.locked[predId]) return;
    setState((s) => ({ ...s, selections: { ...s.selections, [predId]: value } }));
  };

  const lock = useCallback(
    (predId: string) => {
      if (state.locked[predId]) return;
      const chosen = state.selections[predId];
      if (!chosen) return;
      const pred = data.predictions.find((p) => p.id === predId);
      if (!pred) return;
      const isCorrect = chosen === pred.correctValue;

      setState((s) => ({ ...s, locked: { ...s.locked, [predId]: true } }));

      onActivityResponse?.({
        activityType: 'reveal-predict',
        mode: 'quiz',
        responseData: { predictionId: predId, chosen },
        isCorrect,
        score: isCorrect ? 100 : 0,
      });
    },
    [data.predictions, state.locked, state.selections, onActivityResponse],
  );

  const advance = (toIndex: number) => {
    setState((s) => ({ ...s, currentIndex: toIndex }));
  };

  const accent = theme.colors.accent || theme.colors.primary;
  const gaps = total - score;
  const timesWord = total === 1 ? 'time' : 'times';
  const predictionConclusion: ExperienceConclusion = data.conclusion ?? {
    heading: 'So what',
    body:
      gaps > 0
        ? `You predicted ${total} ${timesWord}; ${gaps} surprised you. Those gaps are where your mental model just updated.`
        : `You predicted ${total} ${timesWord} and reality matched each one. Worth asking what would have surprised you.`,
    reflectionPrompt: data.summary?.prompt,
  };

  return (
    <div
      className="h-full w-full overflow-y-auto"
      style={{ background: theme.colors.background, color: theme.colors.text, fontFamily: theme.fonts.body }}
    >
      <div className="mx-auto max-w-[720px] px-6 py-10">
        {/* Header */}
        <header className="text-center mb-8">
          <h1 style={{ ...slideTypographyStyle(theme, 'heading'), color: theme.colors.text, marginBottom: 8 }}>
            {data.title}
          </h1>
          {data.subtitle && (
            <p style={{ color: theme.colors.textMuted, fontSize: '0.95rem' }}>{data.subtitle}</p>
          )}
        </header>

        {/* Prior-knowledge activation: frame this as predicting into the unknown,
            not a quiz. A wrong guess is productive — the gap is the point (§1). */}
        <div
          className="rounded-lg p-4 mb-6 text-sm leading-relaxed"
          style={{ background: `${accent}10`, color: theme.colors.text }}
        >
          Commit your best guess before each reveal. A wrong guess is useful here; the gap between
          what you expect and what actually happens is what sticks.
        </div>

        {/* Progress bar */}
        <div
          className="h-1 rounded-full mb-8 overflow-hidden"
          style={{ background: theme.colors.surface }}
        >
          <div
            className="h-full transition-all duration-500"
            style={{
              background: accent,
              width: `${total > 0 ? Math.round((completed / total) * 100) : 0}%`,
            }}
          />
        </div>

        {/* Predictions */}
        {data.predictions.map((pred, idx) => {
          const isVisible = idx <= state.currentIndex;
          const isCurrent = idx === state.currentIndex;
          const isLocked = !!state.locked[pred.id];
          const selected = state.selections[pred.id];
          const isCorrect = isLocked && selected === pred.correctValue;
          const showReveal = isLocked;

          if (!isVisible) return null;

          return (
            <section
              key={pred.id}
              data-prediction-id={pred.id}
              className="mb-6"
              style={{ opacity: !isCurrent && isLocked ? 0.6 : 1 }}
            >
              <div
                className="rounded-lg p-6 mb-4 border"
                style={{ background: theme.colors.surface, borderColor: `${accent}30` }}
              >
                <span
                  className="inline-block text-xs font-bold tracking-wider uppercase mb-2"
                  style={{ color: accent }}
                >
                  Prediction {idx + 1} of {total}
                </span>
                <h3 style={{ ...slideTypographyStyle(theme, 'subheading'), color: theme.colors.text }}>
                  {pred.question}
                </h3>
                {pred.context && (
                  <p
                    className="mt-3 leading-relaxed text-sm"
                    style={{ color: theme.colors.textMuted }}
                  >
                    {pred.context}
                  </p>
                )}
              </div>

              <div role="radiogroup" aria-label={pred.question} className="flex flex-col gap-2.5 mb-4">
                {pred.options.map((opt) => {
                  const isSelected = selected === opt.value;
                  // Post-lock we mark what ACTUALLY happens — neutrally, as
                  // "reality", not a green "correct answer" (§1: this is not a quiz).
                  const isRealityOption = isLocked && opt.value === pred.correctValue;
                  return (
                    <button
                      key={opt.value}
                      role="radio"
                      aria-checked={isSelected}
                      disabled={isLocked}
                      onClick={() => select(pred.id, opt.value)}
                      className="flex items-center gap-3 px-5 py-4 rounded-lg border-2 text-left transition-all duration-200"
                      style={{
                        background: isSelected ? `${accent}15` : isRealityOption ? `${accent}08` : theme.colors.surface,
                        borderColor: isSelected ? accent : isRealityOption ? `${accent}66` : `${theme.colors.textMuted}33`,
                        color: theme.colors.text,
                        cursor: isLocked ? 'default' : 'pointer',
                        opacity: isLocked && !isSelected && !isRealityOption ? 0.5 : 1,
                      }}
                    >
                      <span
                        className="flex items-center justify-center rounded-full flex-shrink-0"
                        style={{
                          width: 22,
                          height: 22,
                          border: `2px solid ${isSelected || isRealityOption ? accent : `${theme.colors.textMuted}66`}`,
                        }}
                      >
                        <span
                          className="rounded-full"
                          style={{ width: 10, height: 10, background: isSelected ? accent : 'transparent' }}
                        />
                      </span>
                      <span className="flex-1 leading-snug">{opt.text}</span>
                      {isLocked && isSelected && (
                        <span className="text-xs font-semibold uppercase tracking-wide flex-shrink-0" style={{ color: accent }}>
                          your call
                        </span>
                      )}
                      {isRealityOption && !isSelected && (
                        <span className="text-xs font-semibold uppercase tracking-wide flex-shrink-0" style={{ color: accent }}>
                          what happened
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              {!isLocked && (
                <button
                  onClick={() => lock(pred.id)}
                  disabled={!selected}
                  className="w-full px-6 py-3 rounded-lg font-semibold text-sm transition-opacity"
                  style={{
                    background: accent,
                    color: '#fff',
                    cursor: selected ? 'pointer' : 'not-allowed',
                    opacity: selected ? 1 : 0.4,
                  }}
                >
                  Lock in my prediction
                </button>
              )}

              <AnimatePresence>
                {showReveal && (
                  <Reveal variant="up" className="mt-2" spring="gentle">
                    {/* Payoff register: heavier accent rule + larger radius than the setup card */}
                    <div
                      className="border-l-[5px] rounded-r-xl py-4 px-5 mb-3 shadow-sm"
                      style={{
                        background: `${accent}15`,
                        borderColor: accent,
                      }}
                    >
                      <span
                        className="inline-block text-xs font-bold tracking-wider uppercase mb-2"
                        style={{ color: accent }}
                      >
                        The Answer
                      </span>
                      <p className="leading-relaxed text-sm" style={{ color: theme.colors.text }}>
                        {pred.revealAnswer}
                      </p>
                    </div>
                    <div
                      className="px-5 py-3 rounded-xl text-sm mb-3 border"
                      style={{ background: `${accent}10`, borderColor: `${accent}40`, color: theme.colors.text }}
                    >
                      <span className="font-semibold" style={{ color: accent }}>
                        {isCorrect
                          ? 'That matched your prediction. '
                          : 'That is different from what you predicted, and that gap is the point. '}
                      </span>
                      {isCorrect ? pred.matchFeedback : pred.missFeedback}
                    </div>
                    {idx < total - 1 && isCurrent && (
                      <button
                        onClick={() => advance(idx + 1)}
                        className="px-6 py-3 rounded-lg font-semibold text-sm border-2"
                        style={{
                          background: theme.colors.surface,
                          color: accent,
                          borderColor: accent,
                        }}
                      >
                        Next prediction
                      </button>
                    )}
                  </Reveal>
                )}
              </AnimatePresence>
            </section>
          );
        })}

        {/* Close — NOT a score screen. The old "X of N correct" punished the act
            of predicting; this reframes around the gap/surprise and gives the
            reflection a real input (§1). */}
        {allComplete && (
          <Reveal variant="up" className="mt-6">
            <ExperienceClose
              conclusion={predictionConclusion}
              theme={theme}
              values={{ total, matched: score, gaps: total - score }}
              fallback="Look back at where your predictions and reality lined up, and where they didn't."
            />
          </Reveal>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Editing-mode renderer
// ---------------------------------------------------------------------------

function RevealPredictEditor({
  data,
  theme,
  onUpdate,
}: {
  data: RevealPredictSlideData;
  theme: Theme;
  onUpdate: (data: RevealPredictSlideData) => void;
}) {
  const updateField = <K extends keyof RevealPredictSlideData>(key: K, value: RevealPredictSlideData[K]) =>
    onUpdate({ ...data, [key]: value });

  const updatePrediction = (id: string, patch: Partial<RevealPredictSlideData['predictions'][number]>) =>
    onUpdate({
      ...data,
      predictions: data.predictions.map((p) => (p.id === id ? { ...p, ...patch } : p)),
    });

  const addPrediction = () => {
    const newId = crypto.randomUUID();
    onUpdate({
      ...data,
      predictions: [
        ...data.predictions,
        {
          id: newId,
          question: '',
          options: [
            { value: 'a', text: '' },
            { value: 'b', text: '' },
          ],
          correctValue: 'a',
          revealAnswer: '',
          matchFeedback: '',
          missFeedback: '',
        },
      ],
    });
  };

  const removePrediction = (id: string) =>
    onUpdate({ ...data, predictions: data.predictions.filter((p) => p.id !== id) });

  const accent = theme.colors.accent || theme.colors.primary;

  return (
    <div className="h-full w-full overflow-y-auto p-8">
      <div className="mx-auto max-w-[720px]">
        <input
          value={data.title}
          onChange={(e) => updateField('title', e.target.value)}
          placeholder="Prediction set title…"
          className="w-full mb-2 bg-transparent border-0 border-b-2 border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none transition-colors"
          style={{ ...slideTypographyStyle(theme, 'heading'), color: theme.colors.text }}
        />
        <input
          value={data.subtitle}
          onChange={(e) => updateField('subtitle', e.target.value)}
          placeholder="Subtitle (optional)"
          className="w-full mb-8 bg-transparent border-0 border-b border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none text-sm transition-colors"
          style={{ color: theme.colors.textMuted }}
        />

        {data.predictions.map((pred, idx) => (
          <div
            key={pred.id}
            className="rounded-lg border p-5 mb-4"
            style={{ background: theme.colors.surface, borderColor: `${theme.colors.textMuted}33` }}
          >
            <div className="flex items-center justify-between mb-3">
              <span
                className="text-xs font-bold tracking-wider uppercase"
                style={{ color: accent }}
              >
                Prediction {idx + 1}
              </span>
              <button
                onClick={() => removePrediction(pred.id)}
                className="text-xs text-gray-400 hover:text-red-500"
                title="Remove prediction"
              >
                Remove
              </button>
            </div>
            <input
              value={pred.question}
              onChange={(e) => updatePrediction(pred.id, { question: e.target.value })}
              placeholder="Prediction question…"
              className="w-full mb-3 bg-transparent border-0 border-b border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none font-semibold"
              style={{ color: theme.colors.text }}
            />
            <input
              value={pred.context ?? ''}
              onChange={(e) => updatePrediction(pred.id, { context: e.target.value })}
              placeholder="Optional context (background paragraph)"
              className="w-full mb-3 bg-transparent border-0 border-b border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none text-sm"
              style={{ color: theme.colors.textMuted }}
            />
            <div className="space-y-2 mb-3">
              {pred.options.map((opt, optIdx) => (
                <div key={opt.value} className="flex items-center gap-2">
                  <label className="cursor-pointer text-xs" title="Mark as correct">
                    <input
                      type="radio"
                      name={`correct-${pred.id}`}
                      checked={pred.correctValue === opt.value}
                      onChange={() => updatePrediction(pred.id, { correctValue: opt.value })}
                      className="mr-1"
                    />
                    <span style={{ color: pred.correctValue === opt.value ? feedbackStyle(theme, 'correct').border : theme.colors.textMuted }}>
                      {pred.correctValue === opt.value ? '✓ Correct' : 'Correct?'}
                    </span>
                  </label>
                  <input
                    value={opt.text}
                    onChange={(e) => {
                      const next = [...pred.options];
                      next[optIdx] = { ...opt, text: e.target.value };
                      updatePrediction(pred.id, { options: next });
                    }}
                    placeholder={`Option ${opt.value.toUpperCase()}…`}
                    className="flex-1 px-3 py-2 rounded border focus:outline-none focus:ring-2 focus:ring-blue-500"
                    style={{
                      background: theme.colors.background,
                      borderColor: `${theme.colors.textMuted}33`,
                      color: theme.colors.text,
                    }}
                  />
                  {pred.options.length > 2 && (
                    <button
                      onClick={() => {
                        const next = pred.options.filter((_, i) => i !== optIdx);
                        updatePrediction(pred.id, {
                          options: next,
                          correctValue:
                            pred.correctValue === opt.value && next[0] ? next[0].value : pred.correctValue,
                        });
                      }}
                      className="text-gray-300 hover:text-red-500"
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
              {pred.options.length < 4 && (
                <button
                  onClick={() => {
                    // First unused letter a–z; index-based letters collide after
                    // a remove-then-add (duplicate values break keys + correctValue).
                    const used = new Set(pred.options.map((o) => o.value));
                    let value = '';
                    for (let i = 0; i < 26 && !value; i++) {
                      const letter = String.fromCharCode(97 + i);
                      if (!used.has(letter)) value = letter;
                    }
                    if (!value) value = crypto.randomUUID();
                    updatePrediction(pred.id, {
                      options: [...pred.options, { value, text: '' }],
                    });
                  }}
                  className="text-xs text-blue-600 hover:underline"
                >
                  + Add option
                </button>
              )}
            </div>
            <textarea
              value={pred.revealAnswer}
              onChange={(e) => updatePrediction(pred.id, { revealAnswer: e.target.value })}
              placeholder="Reveal explanation — what the answer is and why"
              className="w-full mb-2 px-3 py-2 rounded border text-sm"
              rows={2}
              style={{
                background: theme.colors.background,
                borderColor: `${theme.colors.textMuted}33`,
                color: theme.colors.text,
              }}
            />
            <input
              value={pred.matchFeedback}
              onChange={(e) => updatePrediction(pred.id, { matchFeedback: e.target.value })}
              placeholder="Feedback if correct"
              className="w-full mb-2 px-3 py-2 rounded border text-sm"
              style={{
                background: theme.colors.background,
                borderColor: `${theme.colors.textMuted}33`,
                color: theme.colors.text,
              }}
            />
            <input
              value={pred.missFeedback}
              onChange={(e) => updatePrediction(pred.id, { missFeedback: e.target.value })}
              placeholder="Feedback if incorrect"
              className="w-full px-3 py-2 rounded border text-sm"
              style={{
                background: theme.colors.background,
                borderColor: `${theme.colors.textMuted}33`,
                color: theme.colors.text,
              }}
            />
          </div>
        ))}

        <button
          onClick={addPrediction}
          className="px-4 py-2 rounded-lg border-2 border-dashed text-sm font-medium w-full"
          style={{ borderColor: `${accent}66`, color: accent }}
        >
          + Add prediction
        </button>

        <div className="mt-6">
          <label
            className="block text-xs font-bold tracking-wider uppercase mb-2"
            style={{ color: theme.colors.textMuted }}
          >
            Summary prompt
          </label>
          <textarea
            value={data.summary?.prompt ?? ''}
            onChange={(e) => updateField('summary', { prompt: e.target.value })}
            placeholder="What surprised you most?"
            rows={2}
            className="w-full px-3 py-2 rounded border text-sm"
            style={{
              background: theme.colors.surface,
              borderColor: `${theme.colors.textMuted}33`,
              color: theme.colors.text,
            }}
          />
        </div>
      </div>
    </div>
  );
}
